import { Node, NodeQuery, useEditor, useNode } from "@craftjs/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ReactElement, useEffect, useState } from "react";

import SortableItem from "./sortable-item";

type CraftQuery = ReturnType<typeof useEditor>["query"];
type CraftActions = ReturnType<typeof useEditor>["actions"];

type LayersProps = {
  actions: CraftActions;
  parentId: string;
  query: CraftQuery;
  onItemRender?: (node: Node) => ReactElement;
};

const defaultItemRender = (node: Node) => (
  <div className="ml-2 mr-4 flex items-center">
    {node.displayName || node.id}
  </div>
);

/**
 * Renders node's children as draggable layers, allowing users to reorder children
 */
const Layers = (props: LayersProps) => {
  const [items, setItems] = useState<NodeQuery[]>([]);
  const { actions, onItemRender = defaultItemRender } = props;

  const { childNodes } = useNode((node) => ({
    childNodes: node.getChildNodes(),
  }));

  useEffect(() => {
    if (childNodes?.length > 0) {
      setItems(
        childNodes.reduce((nodes, node) => {
          nodes.push(node);

          return nodes;
        }, [])
      );
    }
  }, [childNodes]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over === null) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    const activeIndex = items.findIndex((item) => item.id === active.id);
    const overIndex = items.findIndex((item) => item.id === over.id);
    const movedNode = items[activeIndex];

    setItems((items) => arrayMove(items, activeIndex, overIndex));

    /**
     * Apparently, when craft Node A is being moved up to position of craft Node B, say from index 3 to 5,
     * instead of placing the A on the place of B, it keeps B on same index 5 and places A on the previous index, 4.
     * So, when the target index is X, we need to use X+1.
     *
     * But when craft Node is being moved down the index hierarchy (from 5 to 3), it just works.
     */
    const actualDestination =
      activeIndex < overIndex ? overIndex + 1 : overIndex;

    window.requestAnimationFrame(() => {
      actions.move(
        movedNode.id,
        movedNode.node.parent as string,
        actualDestination
      );
    });
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableItem key={item.id} id={item.id}>
            {onItemRender(item.node)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default Layers;
